import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';
import { ClientQuoteService } from './service';
import {
    MAINTENANCE_TYPE,
    getDefaultWorkshopAddress,
    getMaintenanceApiTypeLabel,
} from '../../shared/utils/maintenanceTypeHelpers';
import ClientQuoteSearchView from './views/ClientQuoteSearchView';
import ClientQuoteCartView from './views/ClientQuoteCartView';
import ClientQuoteEstimateModalView from './views/ClientQuoteEstimateModalView';
import { FeedbackBanner, PageContainer } from './styles';

const QUOTE_PAGE_VIEWS = Object.freeze({
    SEARCH: 'SEARCH',
    CART: 'CART',
});

const DEFAULT_MAINTENANCE_LOCALIZATION = 'oficina';

function resolveMaintenanceType(maintenanceLocalization) {
    return maintenanceLocalization === 'empresa'
        ? MAINTENANCE_TYPE.INCOMPANY
        : MAINTENANCE_TYPE.OFICINA;
}

const createDefaultFormState = () => ({
    quantity: 1,
    model: '',
    brand: '',
    problem: '',
    observations: '',
    selectedTasks: [],
    maintenanceLocalization: DEFAULT_MAINTENANCE_LOCALIZATION,
    companyAddress: '',
});

const createRequestMeta = () => ({
    quantity: 1,
    model: '',
    brand: '',
    problem: '',
    maintenanceLocalization: DEFAULT_MAINTENANCE_LOCALIZATION,
    companyAddress: '',
});

function buildAutoObservations(formState, language) {
    const lines = [];

    if (formState.quantity) {
        lines.push(`${language.client_quote_cart_qty_label}${formState.quantity}`);
    }

    if (String(formState.model || '').trim()) {
        lines.push(`${language.client_quote_cart_model_label}${formState.model}`);
    }

    if (String(formState.brand || '').trim()) {
        lines.push(`${language.client_quote_cart_brand_label}${formState.brand}`);
    }

    if (String(formState.problem || '').trim()) {
        lines.push(`${language.client_quote_cart_problem_label}${formState.problem}`);
    }

    return lines.join('\n').trim();
}

function normalizeTaskRequest(task, catalogById) {
    if (task && typeof task === 'object' && task.id && task.nome) {
        return {
            id: task.id,
            nome: task.nome,
            valor: task.valor || 0,
        };
    }

    const taskId = Number(task);
    const catalogTask = catalogById.get(taskId);
    if (!catalogTask) return null;

    return {
        id: catalogTask.id,
        nome: catalogTask.descricao || catalogTask.nome,
        valor: catalogTask.valor || 0,
    };
}

function ClientQuotePage({ currentLanguage }) {
    const clientLanguage = currentLanguage.bepiService.client;

    const [view, setView] = useState(QUOTE_PAGE_VIEWS.SEARCH);
    const [catalogServices, setCatalogServices] = useState([]);
    const [quotationItems, setQuotationItems] = useState([]);

    const [selectedService, setSelectedService] = useState(null);
    const [isEstimateModalOpen, setIsEstimateModalOpen] = useState(false);
    const [editingItemId, setEditingItemId] = useState(null);
    const [formState, setFormState] = useState(createDefaultFormState());
    const [requestMeta, setRequestMeta] = useState(createRequestMeta());

    const [isLoadingData, setIsLoadingData] = useState(true);
    const [isSendingQuote, setIsSendingQuote] = useState(false);
    const [fetchError, setFetchError] = useState('');
    const [actionError, setActionError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const loadInitialData = useCallback(async () => {
        setIsLoadingData(true);
        setFetchError('');

        const [servicesResponse] = await Promise.allSettled([
            ClientQuoteService.getCatalogServices(),
        ]);

        let nextError = '';

        if (servicesResponse.status === 'fulfilled') {
            if (!isMountedRef.current) return;
            setCatalogServices(Array.isArray(servicesResponse.value) ? servicesResponse.value : []);
        } else {
            nextError = clientLanguage.client_quote_catalog_error;
        }

        if (nextError) {
            if (!isMountedRef.current) return;
            setFetchError(nextError);
        }

        if (!isMountedRef.current) return;
        setIsLoadingData(false);
    }, [clientLanguage]);

    useEffect(() => {
        loadInitialData();
    }, [loadInitialData]);

    useEffect(() => {
        if (!successMessage) return undefined;

        const timeoutId = setTimeout(() => {
            setSuccessMessage('');
        }, 3000);

        return () => clearTimeout(timeoutId);
    }, [successMessage]);

    const availableTasks = useMemo(() => {
        return catalogServices.map((service) => ({
            id: service.id,
            nome: service.descricao || service.nome,
            descricao: service.descricao,
            valor: service.valor,
            codigoServico: service.codigo_servico,
        }));
    }, [catalogServices]);

    useEffect(() => {
        if (!isEstimateModalOpen || editingItemId) return;

        setFormState((previous) => {
            const nextObservations = buildAutoObservations(previous, clientLanguage);

            if (previous.observations === nextObservations) {
                return previous;
            }

            return {
                ...previous,
                observations: nextObservations,
            };
        });
    }, [
        clientLanguage,
        editingItemId,
        formState.brand,
        formState.model,
        formState.problem,
        formState.quantity,
        isEstimateModalOpen,
    ]);

    const showSearchView = useCallback(() => {
        setView(QUOTE_PAGE_VIEWS.SEARCH);
    }, []);

    const showCartView = useCallback(() => {
        setView(QUOTE_PAGE_VIEWS.CART);
    }, []);

    const closeEstimateModal = useCallback(() => {
        setIsEstimateModalOpen(false);
        setSelectedService(null);
        setEditingItemId(null);
        setFormState(createDefaultFormState());
    }, []);

    const openCreateModal = useCallback((service) => {
        setSelectedService(service);
        setEditingItemId(null);
        setFormState(createDefaultFormState());
        setIsEstimateModalOpen(true);
    }, []);

    const openEditModal = useCallback((item) => {
        setSelectedService({
            id: item.equipamentoId,
            nome: item.equipamento,
            descricao: item.equipamento,
            categoria_id: item.categoria_id,
            produto_id: item.produto_id,
            categoria_nome: item.categoria_nome,
            produto_nome: item.produto_nome,
        });
        setEditingItemId(item.id);
        setFormState({
            quantity: item.quantity || 1,
            model: item.model || '',
            brand: item.brand || '',
            problem: item.problem || '',
            observations: item.observations || '',
            selectedTasks: item.selectedTasks || [],
            maintenanceLocalization: item.maintenanceLocalization || DEFAULT_MAINTENANCE_LOCALIZATION,
            companyAddress: item.companyAddress || '',
        });
        setIsEstimateModalOpen(true);
    }, []);

    const handleFieldChange = useCallback((field, value) => {
        setFormState((previous) => ({
            ...previous,
            [field]: value,
        }));
    }, []);

    const handleSaveQuotationItem = useCallback((itemData) => {
        if (!selectedService) return;

        const equipmentName = selectedService.nome || selectedService.descricao || '-';

        const normalizedItem = {
            equipamentoId: selectedService.id,
            categoria_id: selectedService.categoria_id,
            produto_id: selectedService.produto_id,
            equipamento: equipmentName,
            categoria_nome: selectedService.categoria_nome,
            produto_nome: selectedService.produto_nome,
            selectedTasks: itemData.selectedTasks || [],
            quantity: itemData.quantity,
            model: itemData.model,
            brand: itemData.brand,
            problem: itemData.problem,
            observations: itemData.observations,
            maintenanceLocalization: itemData.maintenanceLocalization,
            tipoManutencao: getMaintenanceApiTypeLabel(resolveMaintenanceType(itemData.maintenanceLocalization)),
            companyAddress: itemData.maintenanceLocalization === 'empresa' ? itemData.companyAddress : '',
        };

        if (editingItemId) {
            setQuotationItems((previous) =>
                previous.map((item) =>
                    item.id === editingItemId
                        ? {
                            ...item,
                            ...normalizedItem,
                        }
                        : item
                )
            );
        } else {
            setQuotationItems((previous) => [
                ...previous,
                {
                    id: Date.now(),
                    timestamp: new Date().toISOString(),
                    ...normalizedItem,
                },
            ]);
        }

        setRequestMeta({
            quantity: itemData.quantity,
            model: itemData.model,
            brand: itemData.brand,
            problem: itemData.problem,
            maintenanceLocalization: itemData.maintenanceLocalization,
            companyAddress: itemData.companyAddress,
        });

        setSuccessMessage('');
        setActionError('');
        closeEstimateModal();
    }, [closeEstimateModal, editingItemId, selectedService]);

    const handleDeleteItem = useCallback((itemId) => {
        setQuotationItems((previous) => {
            const nextItems = previous.filter((item) => item.id !== itemId);
            if (nextItems.length === 0) {
                setView(QUOTE_PAGE_VIEWS.SEARCH);
            }
            return nextItems;
        });
    }, []);

    const handleSendQuotation = useCallback(async () => {
        if (quotationItems.length === 0) {
            setActionError(clientLanguage.client_quote_empty_error);
            setSuccessMessage('');
            return;
        }

        setIsSendingQuote(true);
        setActionError('');
        setSuccessMessage('');

        try {
            const catalogById = new Map(catalogServices.map((service) => [Number(service.id), service]));
            const uniqueTasksMap = new Map();

            quotationItems.forEach((item) => {
                (item.selectedTasks || []).forEach((task) => {
                    const taskRequest = normalizeTaskRequest(task, catalogById);
                    if (!taskRequest) return;

                    uniqueTasksMap.set(taskRequest.servico_id, taskRequest);
                });
            });

            const quotationData = {
                servicos: Array.from(uniqueTasksMap.values()),
                quantity: requestMeta.quantity || 1,
                model: requestMeta.model || '',
                brand: requestMeta.brand || '',
                problem: requestMeta.problem || '',
                maintenanceLocalization: requestMeta.maintenanceLocalization || DEFAULT_MAINTENANCE_LOCALIZATION,
                endereco:
                    requestMeta.maintenanceLocalization === 'empresa'
                        ? requestMeta.companyAddress
                        : getDefaultWorkshopAddress(),
                tipo: 'cotacao',
                equipmentIds: quotationItems.map((item) => item.equipamentoId),
                equipamentos: quotationItems.map((item) => ({
                    id: item.equipamentoId,
                    categoria_id: item.categoria_id,
                    produto_id: item.produto_id,
                    nome: item.equipamento,
                })),
            };

            await ClientQuoteService.createQuotation(quotationData);

            if (!isMountedRef.current) return;
            
            Swal.fire({
                title: currentLanguage.bepiService.shared.shared_details_success_label,
                text: clientLanguage.client_quote_success_saving_quote_msg,
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });

            setQuotationItems([]);
            setView(QUOTE_PAGE_VIEWS.SEARCH);
            setFormState(createDefaultFormState());
            setRequestMeta(createRequestMeta());
        } catch (requestError) {
            if (!isMountedRef.current) return;
            setActionError(requestError?.message || clientLanguage.client_quote_send_error);
        } finally {
            if (!isMountedRef.current) return;
            setIsSendingQuote(false);
        }
    }, [catalogServices, clientLanguage, currentLanguage, quotationItems, requestMeta]);

    return (
        <PageContainer>
            {actionError ? <FeedbackBanner $variant='error'>{actionError}</FeedbackBanner> : null}
            {successMessage ? <FeedbackBanner $variant='success'>{successMessage}</FeedbackBanner> : null}

            {view === QUOTE_PAGE_VIEWS.SEARCH ? (
                <ClientQuoteSearchView
                    cartItemCount={quotationItems.length}
                    onServiceSelect={openCreateModal}
                    onViewCart={showCartView}
                    loading={isLoadingData}
                    error={fetchError}
                    currentLanguage={currentLanguage}
                />
            ) : null}

            {view === QUOTE_PAGE_VIEWS.CART ? (
                <ClientQuoteCartView
                    items={quotationItems}
                    availableTasks={availableTasks}
                    onAddNew={showSearchView}
                    onEditItem={openEditModal}
                    onDeleteItem={handleDeleteItem}
                    onSend={handleSendQuotation}
                    loading={isSendingQuote}
                    currentLanguage={currentLanguage}
                />
            ) : null}

            {isEstimateModalOpen && selectedService ? (
                <ClientQuoteEstimateModalView
                    open={isEstimateModalOpen}
                    service={selectedService}
                    formState={formState}
                    availableTasks={availableTasks}
                    isEditMode={Boolean(editingItemId)}
                    loading={isSendingQuote}
                    currentLanguage={currentLanguage}
                    onClose={closeEstimateModal}
                    onSave={handleSaveQuotationItem}
                    onFieldChange={handleFieldChange}
                />
            ) : null}
        </PageContainer>
    );
}

ClientQuotePage.propTypes = {
    currentLanguage: PropTypes.object.isRequired,
};

export default ClientQuotePage;
